import React, { useState, useEffect } from 'react';
import { Award, Download, FileText } from 'lucide-react';
import { Card, Badge, Button, ComponentBadge, Spinner, useIsMobile } from '../components/ui';
import PageLayout from '../components/layout/PageLayout';
import api from '../utils/api';

const TIER_COLORS = {
  Bronze: '#B08D57',
  Silver: '#9CA3AF',
  Gold: '#D4A017',
  Platinum: '#7C6FBE',
};

export default function DonorCertificates() {
  const [badge, setBadge] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    Promise.all([
      api.get('/donors/badge').catch(() => null),
      api.get('/donors/certificates').catch(() => ({ data: [] })),
    ]).then(([b, c]) => {
      if (b) setBadge(b.data);
      setCertificates(c.data);
    }).finally(() => setLoading(false));
  }, []);

  const downloadCertificate = async (cert) => {
    setDownloadingId(cert.id);
    try {
      const { data } = await api.get(`/donors/certificates/${cert.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${cert.certificate_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Could not download certificate. Try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) return (
    <PageLayout>
      <div style={{ display: 'flex', justifyContent: 'center', padding: '80px' }}><Spinner /></div>
    </PageLayout>
  );

  const tierColor = badge?.tier ? TIER_COLORS[badge.tier] : 'var(--gray-400)';
  const progressPct = badge?.next_tier
    ? Math.min(100, Math.round(((badge.total_donations) / (badge.total_donations + badge.donations_to_next_tier)) * 100))
    : 100;

  return (
    <PageLayout>
      <div style={{ animation: 'bb-fadeUp 0.5s ease both' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 800, letterSpacing: '-0.03em', color: 'var(--gray-900)', marginBottom: '16px' }}>
          My Certificates
        </h1>

        {/* Badge banner */}
        <div style={{
          background: `linear-gradient(135deg, ${tierColor} 0%, ${tierColor}cc 100%)`,
          borderRadius: 'var(--radius-lg)', padding: isMobile ? '18px' : '24px',
          display: 'flex', alignItems: 'center', gap: '18px', marginBottom: '18px',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', right: '-30px', top: '-30px', width: '160px', height: '160px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          <div style={{
            width: '58px', height: '58px', borderRadius: '50%',
            background: 'rgba(255,255,255,0.22)', backdropFilter: 'blur(4px)',
            border: '2px solid rgba(255,255,255,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Award size={28} color="white" />
          </div>
          <div style={{ flex: 1, zIndex: 1 }}>
            <div style={{ fontSize: '19px', fontWeight: 800, color: 'white', marginBottom: '4px' }}>
              {badge?.tier ? `${badge.tier} Donor` : 'No badge yet'}
            </div>
            <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.9)', marginBottom: '10px' }}>
              {badge?.total_donations ?? 0} total donation{badge?.total_donations === 1 ? '' : 's'}
              {badge?.next_tier && ` · ${badge.donations_to_next_tier} more to reach ${badge.next_tier}`}
            </div>
            <div style={{ height: '6px', borderRadius: '4px', background: 'rgba(255,255,255,0.25)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progressPct}%`, background: 'white', borderRadius: '4px', transition: 'width 0.4s ease' }} />
            </div>
          </div>
        </div>

        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
            <FileText size={16} color="var(--red)" />
            <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--gray-900)' }}>Donation Certificates</div>
          </div>

          {certificates.length === 0 ? (
            <p style={{ fontSize: '13.5px', color: 'var(--gray-500)', padding: '12px 0' }}>
              No certificates yet — once a recipient confirms your donation, your certificate appears here.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {certificates.map(cert => (
                <div
                  key={cert.id}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
                    padding: '13px 14px', background: 'var(--gray-50)', borderRadius: '9px',
                    border: '1px solid var(--gray-100)', flexWrap: isMobile ? 'wrap' : 'nowrap',
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--gray-900)' }}>{cert.certificate_number}</span>
                      <Badge color="red">{cert.blood_type}</Badge>
                      <ComponentBadge component={cert.component} />
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--gray-500)' }}>
                      {cert.hospital_name || 'Blood donation'} · {new Date(cert.issued_at).toLocaleDateString()} · {cert.units} unit{cert.units !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    loading={downloadingId === cert.id}
                    onClick={() => downloadCertificate(cert)}
                  >
                    <Download size={14} /> Download
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </PageLayout>
  );
}
